import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { Input } from '@workspace/ui/components/input'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Coins, Plus } from 'lucide-react'
import { useState } from 'react'

interface CreditTransaction {
  id: string
  amount: number
  type: string
  note: string | null
  adminUserName: string | null
  createdAt: string | Date
}

interface AdminCreditsSectionProps {
  balance: number | undefined
  transactions: CreditTransaction[] | undefined
  isLoading: boolean
  onAddCredits: (amount: number, note?: string) => Promise<void>
  isAdding: boolean
}

export function AdminCreditsSection({
  balance,
  transactions,
  isLoading,
  onAddCredits,
  isAdding,
}: AdminCreditsSectionProps) {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [showForm, setShowForm] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const numAmount = Number.parseInt(amount, 10)
    if (numAmount <= 0) return

    await onAddCredits(numAmount, note || undefined)
    setAmount('')
    setNote('')
    setShowForm(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Coins className="size-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Credits</h3>
          <Badge variant="secondary" className="text-base">
            {balance ?? 0}
          </Badge>
        </div>
        {!showForm && (
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            <Plus className="size-4 mr-1" />
            Add Credits
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-3 p-4 rounded-lg border border-border bg-card"
        >
          <div className="space-y-1">
            <label htmlFor="credits-amount" className="text-sm font-medium text-muted-foreground">
              Amount
            </label>
            <Input
              id="credits-amount"
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="10"
              className="w-24"
              required
            />
          </div>
          <div className="flex-1 space-y-1">
            <label htmlFor="credits-note" className="text-sm font-medium text-muted-foreground">
              Note (optional)
            </label>
            <Input
              id="credits-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for adding credits"
            />
          </div>
          <Button type="submit" disabled={isAdding || !amount || Number(amount) <= 0} size="sm">
            {isAdding ? 'Adding...' : 'Add'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
        </form>
      )}

      {transactions && transactions.length > 0 ? (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Date</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Type</th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground">Amount</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Note</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Admin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="px-4 py-2 text-muted-foreground">
                    {new Date(tx.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={tx.type === 'ADMIN_TOPUP' ? 'secondary' : 'outline'}>
                      {tx.type === 'ADMIN_TOPUP' ? 'Grant' : 'Usage'}
                    </Badge>
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{tx.note ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{tx.adminUserName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground py-4">No credit transactions yet.</p>
      )}
    </div>
  )
}
